import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Patient, PatientStatus } from '@ffd/api-client';
import { AvatarsComponent } from '@libs/ui-lib';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { routes } from '@libs/utils';
import { Participant, ParticipantListItem, SearchParticipantsComponent } from '../search-participants';
import { SelectedParticipantComponent } from '../selected-participant';
import { LinkPatientDialogService } from './link-patient-dialog.service';
import { LinkPatientDialogData, LinkPatientForm } from './link-patient.interface';

@Component({
  selector: 'lib-link-patient',
  imports: [
    CommonModule,
    FormsModule,
    SearchParticipantsComponent,
    SvgIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    SelectedParticipantComponent,
    AvatarsComponent,
  ],
  providers: [LinkPatientDialogService],
  templateUrl: './link-patient-dialog.component.html',
  styleUrl: './link-patient-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkPatientDialogComponent implements OnInit {
  protected dialogData: LinkPatientDialogData = inject(DIALOG_DATA);
  private readonly screenService = inject(LinkPatientDialogService);
  private readonly dialogRef = inject(DialogRef);

  protected readonly form = new FormGroup<LinkPatientForm>({
    patient: new FormControl(null, [Validators.required]),
  });
  protected readonly patientParticipant = computed(() => this.screenService.viewModel().patientParticipant);
  protected readonly allPatients = computed(() => this.screenService.viewModel().allPatients);
  protected readonly patients = computed(() => this.screenService.viewModel().patientOptions as Participant[]);
  protected readonly selectedPatient = computed(() => this.screenService.viewModel().patientParticipant);
  protected readonly patientsLoading = computed(() => this.screenService.viewModel().patientsLoading);
  protected readonly isShowcase = computed(() => this.screenService.viewModel().isShowcase);
  public routes = routes;

  public ngOnInit(): void {
    if (this.dialogData.patient) {
      this.form.controls.patient.setValue(this.dialogData.patient);
    }

    this.screenService.init();
  }

  protected closeDialog(): void {
    this.dialogRef.close();
  }

  protected saveLinkedPatient(): void {
    this.dialogRef.close({
      patient: this.patientParticipant(),
    });
  }

  protected onSearchPatients(searchTerm: string | null): void {
    this.screenService.updatePatientSearchTerm(searchTerm);
  }

  protected onPatientsEndReached(): void {
    this.screenService.searchPatients({ searchTerm: undefined });
  }

  protected onAddPatientParticipant(participant: ParticipantListItem | Patient): void {
    const patientParticipant = participant as Patient;
    this.screenService.setPatientParticipant({
      id: patientParticipant.id,
      userId: patientParticipant.userId,
      firstName: patientParticipant.firstName,
      lastName: patientParticipant.lastName,
      pmsPatientId: patientParticipant.pmsPatientId,
      status: patientParticipant.status ?? PatientStatus.NOT_INVITED,
      participantRole: 'patient',
    });
  }

  protected onRemovePatientParticipant(): void {
    this.screenService.removePatientParticipant();
  }
}
